import React, {useMemo} from 'react'
import {BoxProps, Flex, Text, TextProps, useMultiStyleConfig, VStack} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {Tabs, TextItem, ImageItem} from '_uiComponents'
import {FullProductType, ProductVariationGroup} from '_types'
import {capitalize, type PageContentComponent} from '_utils'

interface ProductInfoProps {
    activeIndex?: number
    product: FullProductType
    activeVariation: ProductVariationGroup
    pageContent: Record<string, any[]>
    onTabChange?: (index: number) => void
}

// Relative CMS links (e.g. "/shippinginformation") are missing the current locale segment required by the app router.
const withLocalePrefix = (link: string, localeAlias: string): string =>
    link && link.startsWith('/') && link.indexOf(`/${localeAlias}/`) === -1 ? `/${localeAlias}${link}` : link

// Treats whitespace-only strings as empty, so blank CMS/product values don't render an empty element.
// Some fields can come back as non-string values (e.g. numbers) at runtime despite their declared type, hence the typeof guard.
const hasContent = (value?: string): value is string => typeof value === 'string' && value.trim().length > 0

// A CMS textItem/imageItem whose name contains "title" is a section heading (paragraphXl), everything else is body text.
const isTitleComponent = (component?: PageContentComponent): boolean => (component?.name?.toLowerCase().indexOf('title') ?? -1) !== -1

// Splits a flat CMS item list into blocks that each start at a "title" component and include the following
// non-title items, so a heading + its body stay grouped. Leading non-title items (no heading yet) form their
// own first block. Used to reproduce the Figma layout where each heading/body block is separated by a larger gap.
const groupByTitle = (components: PageContentComponent[]): PageContentComponent[][] => {
    const blocks: PageContentComponent[][] = []
    components.forEach((component) => {
        if (isTitleComponent(component) || blocks.length === 0) {
            blocks.push([component])
        } else {
            blocks[blocks.length - 1].push(component)
        }
    })
    return blocks
}

// Renders a Page Designer region (textBox/imageBox/textItem/imageItem entries) as a content list.
// Shared by the CMS-driven tabs (Confezione, Spedizione e Resi).
// When `grouped` is set, heading/body blocks are separated by `sectionStyle` gap (56px) while the items
// inside a block use `blockStyle` gap (12px) — matching Figma Spedizione e Resi (5655-4360 / 5847-11445).
const PageContentSection = ({
    components,
    styles,
    title,
    sectionStyle,
    grouped,
    blockStyle,
}: {
    components?: PageContentComponent[]
    styles: Record<string, BoxProps & TextProps>
    title?: string
    sectionStyle?: BoxProps
    grouped?: boolean
    blockStyle?: BoxProps
}) => {
    const {locale} = useMultiSite()

    const renderComponent = (component: PageContentComponent, index: number) => {
        if (component?.type === 'image') {
            const {imageDesktop, imageTablet, imageMobile} = component
            return <ImageItem imageDesktop={imageDesktop} imageTablet={imageTablet} imageMobile={imageMobile} key={index} />
        }

        // "button" components (e.g. bannerNewsItem CTAs) are text with a single {0} placeholder resolved to a link, same as "text".
        if ((component?.type === 'text' || component?.type === 'button') && (component.text || component.links)) {
            const links = component.links?.map((link) => ({
                title: link.title ?? '',
                link: withLocalePrefix(link.link ?? '', locale.alias),
            }))
            return (
                <TextItem
                    text={component.text ?? ''}
                    links={links}
                    {...(isTitleComponent(component) ? styles.tabSectionTitle : styles.tabText)}
                    key={index}
                />
            )
        }

        return null
    }

    if (grouped && components?.length) {
        return (
            <VStack {...(sectionStyle ?? styles.tabSection)}>
                {title && <Text {...styles.tabSectionTitle}>{title}</Text>}
                {groupByTitle(components).map((block, blockIndex) => (
                    <VStack {...(blockStyle ?? styles.tabSubSection)} key={blockIndex}>
                        {block.map((component, index) => renderComponent(component, index))}
                    </VStack>
                ))}
            </VStack>
        )
    }

    return (
        <VStack {...(sectionStyle ?? styles.tabSection)}>
            {title && <Text {...styles.tabSectionTitle}>{title}</Text>}
            {components?.map((component, index) => renderComponent(component, index))}
        </VStack>
    )
}

/**
 * @name ProductInfo
 *
 * @component
 *
 * @description
 * Renders the "Descrizione / Confezione / Spedizione e Resi" drawer content for the PDP, opened
 * from the info CTAs below the size selector. Structure only for now: the "Descrizione" tab uses
 * product/variation data already available, while "Confezione" and "Spedizione e Resi" render
 * whatever Page Designer content is mapped to their regions.
 *
 * @returns {React.FC} - The rendered product info tabs.
 */
const ProductInfo = ({activeIndex = 0, product, activeVariation, pageContent, onTabChange}: ProductInfoProps) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('ProductInfo', {}) as Record<string, BoxProps & TextProps>

    const LABEL_DESCRIPTION_TAB = capitalize(formatMessage({id: 'label.description', defaultMessage: 'DESCRIPTION'}))
    const LABEL_PACKAGING_TAB = capitalize(formatMessage({id: 'label.packaging', defaultMessage: 'PACKAGING'}))
    const LABEL_SHIPPING_RETURNS_TAB = capitalize(formatMessage({id: 'label.shipping.returns', defaultMessage: 'SHIPPING AND RETURNS'}))

    const LABEL_DESCRIPTION = capitalize(formatMessage({id: 'label.description', defaultMessage: 'Description'}))
    const LABEL_DETAILS = capitalize(formatMessage({id: 'label.details', defaultMessage: 'DETAILS'}))
    const LABEL_MATERIALS_AND_DETAILS = capitalize(formatMessage({id: 'label.materials.and.details', defaultMessage: 'MATERIALS AND DETAILS'}))

    const LABEL_SKU = formatMessage({id: 'label.pdp.details.sku', defaultMessage: 'SKU: {0}'}, {0: product?.id ?? ''})
    const LABEL_COLOR = formatMessage({id: 'label.pdp.details.color', defaultMessage: 'Color: {0}'}, {0: activeVariation?.color?.label ?? ''})

    const asset_packging_id = activeVariation?.assetPackagingID || null

    // Packaging region can hold multiple PWA_packaging_pdp components; pick the one whose name matches the variation's assetPackagingID.
    const packagingItems = useMemo(() => {
        const packagingComponents = pageContent?.['packaging'] ?? []
        const matchedComponent = packagingComponents.find(
            (component) =>
                component?.name && asset_packging_id && (component.name.indexOf(asset_packging_id) !== -1 || asset_packging_id.indexOf(component.name) !== -1),
        )
        return matchedComponent?.items ?? []
    }, [pageContent, asset_packging_id])

    // Shipment region holds shipping info plus separate return components for the standard vs unreturnable (e.g. fragrances) flows;
    // only the return-related components need to be filtered by the unreturnable state, other components are kept as-is.
    const shippingReturnsItems = useMemo(() => {
        const shipmentComponents = pageContent?.['shipment'] ?? []
        return shipmentComponents
            .filter((component) => {
                const name = component?.name?.toLowerCase() ?? ''
                const isReturnComponent = name.indexOf('return') !== -1
                if (!isReturnComponent) {
                    return true
                }
                return activeVariation?.unreturnable ? name.indexOf('unreturnable') !== -1 : name === 'return'
            })
            .flatMap((component) => component?.items ?? [])
    }, [pageContent, activeVariation?.unreturnable])

    const LABEL_PRODUCT_FROM_SOLOMEO = formatMessage({
        id: 'label.pdp.madeinitaly',
        defaultMessage:
            'Our items are manufactured in the hamlet of Solomeo, in Italy, respecting human beings and labor dignity and  in harmony with the land.',
    })
    const descriptionTab = useMemo(
        () => (
            <VStack {...styles.tabSection}>
                <VStack {...styles.tabSubSection}>
                    <Text {...styles.tabSectionTitle}>{LABEL_DESCRIPTION}</Text>
                    <Text {...styles.tabModelName}>{product?.shortDescription}</Text>
                    {hasContent(product?.longDescription) && <Text dangerouslySetInnerHTML={{__html: product.longDescription}} {...styles.tabText} />}
                    {activeVariation?.materials?.showProductFromSolomeo && <Text {...styles.tabText}>{LABEL_PRODUCT_FROM_SOLOMEO}</Text>}

                    <Text {...styles.tabTextBold}>{LABEL_SKU}</Text>
                    <Text {...styles.tabTextBold}>{LABEL_COLOR}</Text>
                </VStack>
                {activeVariation?.materials && (
                    <VStack {...styles.tabSubSectionMaterials}>
                        <Text {...styles.tabSectionTitle}>{LABEL_MATERIALS_AND_DETAILS}</Text>
                        {hasContent(activeVariation?.materials?.care) && (
                            <Text dangerouslySetInnerHTML={{__html: activeVariation.materials.care}} {...styles.tabText} />
                        )}
                        {(hasContent(activeVariation?.materials?.washing) || hasContent(activeVariation?.materials?.washingInfo)) && (
                            <Text
                                dangerouslySetInnerHTML={{
                                    __html: `${activeVariation?.materials?.washing ?? ''}${activeVariation?.materials?.washingInfo ?? ''}`,
                                }}
                                {...styles.tabText}
                            />
                        )}
                    </VStack>
                )}
                <VStack {...styles.tabSubSection}>
                    {activeVariation && !activeVariation.materials && activeVariation.details?.details && (
                        <Text {...styles.tabSectionTitle}>{LABEL_DETAILS}</Text>
                    )}
                    {hasContent(activeVariation?.details?.details) && (
                        <Text
                            dangerouslySetInnerHTML={{
                                __html: (() => {
                                    const lines = activeVariation.details.details.split('\n')
                                    // Figma (5655-4321 / 5847-11406): 5px spacing between detail lines; the last line has none.
                                    return lines
                                        .map((line, i) => `<div style="${i < lines.length - 1 ? 'margin-bottom:5px' : ''}">${line}</div>`)
                                        .join('')
                                })(),
                            }}
                            {...styles.tabText}
                        />
                    )}
                    {hasContent(activeVariation?.details?.materials) && (
                        <Text {...styles.tabTextBold} dangerouslySetInnerHTML={{__html: activeVariation.details.materials}} />
                    )}
                </VStack>
            </VStack>
        ),
        [product, activeVariation, pageContent],
    )

    const tabs = useMemo(
        () => [
            {label: LABEL_DESCRIPTION_TAB, content: descriptionTab},
            {
                label: LABEL_PACKAGING_TAB,
                content: (
                    <PageContentSection components={packagingItems} styles={styles} title={LABEL_PACKAGING_TAB} sectionStyle={styles.tabSectionPackaging} />
                ),
            },
            {
                label: LABEL_SHIPPING_RETURNS_TAB,
                content: (
                    <PageContentSection
                        components={shippingReturnsItems}
                        styles={styles}
                        grouped
                        sectionStyle={styles.tabSectionShipmentReturns}
                        blockStyle={styles.tabSubSectionShipmentReturns}
                    />
                ),
            },
        ],
        [descriptionTab, pageContent, packagingItems, shippingReturnsItems],
    )

    return (
        <Flex {...styles.container} data-testid="pdp-product-info">
            <Tabs tabs={tabs} variant="sizeGuide" activeIndex={activeIndex} onTabChange={onTabChange} enableDefaultPanels />
        </Flex>
    )
}

export default ProductInfo
